import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Stack,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import Swal from "sweetalert2";
import { auth, db } from "../firebase";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  onSnapshot,
  Timestamp,
  getDoc,
} from "firebase/firestore";

/* ================= TOAST ================= */
const Toast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 2500,
});

/* ================= COMPONENT ================= */
export default function StaffDetails() {
  const [propertyId, setPropertyId] = useState(null);
  const [staffs, setStaffs] = useState([]);

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);

  const [form, setForm] = useState({
    id: "",
    name: "",
    mobile: "",
    address: "",
    monthpayment: "",
  });

  /* ================= AUTH + PROPERTY ================= */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return;

      const roleSnap = await getDoc(doc(db, "authroles", user.uid));
      if (!roleSnap.exists()) {
        Toast.fire({ icon: "error", title: "Property not assigned" });
        return;
      }

      setPropertyId(roleSnap.data().propertyId);
    });

    return () => unsub();
  }, []);

  /* ================= FETCH STAFF ================= */
  useEffect(() => {
    if (!propertyId) return;

    return onSnapshot(
      query(
        collection(db, "staffs"),
        where("propertyId", "==", propertyId)
      ),
      (snap) => {
        setStaffs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );
  }, [propertyId]);

  /* ================= ADD STAFF ================= */
  const handleAdd = async () => {
    if (!form.name) {
      Toast.fire({ icon: "error", title: "Enter staff name" });
      return;
    }

    await addDoc(collection(db, "staffs"), {
      ...form,
      propertyId,
      createdAt: Timestamp.now(),
    });

    Toast.fire({ icon: "success", title: "Staff Added" });
    setOpenAdd(false);
    setForm({ id: "", name: "", mobile: "", address: "", monthpayment: "" });
  };

  /* ================= UPDATE STAFF ================= */
  const handleUpdate = async () => {
    await updateDoc(doc(db, "staffs", form.id), {
      name: form.name,
      mobile: form.mobile,
      address: form.address,
      monthpayment: form.monthpayment,
      updatedAt: Timestamp.now(),
    });

    Toast.fire({ icon: "success", title: "Staff Updated" });
    setOpenEdit(false);
  };

  /* ================= DELETE STAFF ================= */
  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "staffs", id));
    Toast.fire({ icon: "success", title: "Staff Deleted" });
  };

  return (
    <Box  className=" p-3">
      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Typography variant="h5" fontWeight="bold">
              👨‍💼 Staff Management
            </Typography>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAdd(true)}
            >
              Add Staff
            </Button>
          </Stack>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Monthly Payment</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {staffs.map((s, i) => (
                  <TableRow key={s.id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{s.name}</TableCell>
                    <TableCell>{s.mobile}</TableCell>
                    <TableCell>{s.address}</TableCell>
                    <TableCell>₹ {s.monthpayment}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        onClick={() => {
                          setForm(s);
                          setOpenEdit(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>

                      <IconButton
                        color="error"
                        onClick={() => handleDelete(s.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ================= ADD DIALOG ================= */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} fullWidth>
        <DialogTitle>Add Staff</DialogTitle>
        <DialogContent>
          {["name", "mobile", "address", "monthpayment"].map((f) => (
            <TextField
              key={f}
              label={f.toUpperCase()}
              fullWidth size="small"
              margin="dense"
              value={form[f]}
              onChange={(e) => setForm({ ...form, [f]: e.target.value })}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= EDIT DIALOG ================= */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth>
        <DialogTitle>Edit Staff</DialogTitle>
        <DialogContent>
          {["name", "mobile", "address", "monthpayment"].map((f) => (
            <TextField
              key={f}
              label={f.toUpperCase()}
              fullWidth
              margin="dense"
              value={form[f]}
              onChange={(e) => setForm({ ...form, [f]: e.target.value })}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
